import React, { useState } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Form,
  Input,
  Select,
  Switch,
  Drawer,
  Empty,
  Popconfirm,
  Alert,
  DatePicker,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  NCRRecord,
  NCRRootCauseAnalysis,
} from '../../services/quality.service';
import {
  useAddRootCause,
  useUpdateRootCause,
  useDeleteRootCause,
} from '../../hooks/useQuality';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

const RCA_METHODS = ['5 Whys', 'Fishbone (Ishikawa)', 'FMEA', 'Fault Tree', 'Pareto', 'Other'];

type Props = {
  ncr: NCRRecord;
  ncrId: string;
  onChanged: () => void;
};

const NCRRootCausePanel: React.FC<Props> = ({ ncr, ncrId, onChanged }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<NCRRootCauseAnalysis | null>(null);
  const [form] = Form.useForm();
  const addMutation = useAddRootCause();
  const updateMutation = useUpdateRootCause();
  const deleteMutation = useDeleteRootCause();

  const canEditStatus = ncr.status !== 'Closed' && ncr.status !== 'Rejected';
  const rows = ncr.root_causes?.length
    ? ncr.root_causes
    : ncr.root_cause_analysis
      ? ([
          {
            id: 'legacy',
            sequence: 1,
            root_cause_analysis: ncr.root_cause_analysis,
            root_cause_method: ncr.root_cause_method,
            root_cause_date: ncr.root_cause_date,
            root_cause_verified: ncr.root_cause_verified,
          },
        ] as NCRRootCauseAnalysis[])
      : [];

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      root_cause_date: dayjs(),
      root_cause_verified: false,
      root_cause_method: '5 Whys',
    });
    setDrawerOpen(true);
  };

  const openEdit = (row: NCRRootCauseAnalysis) => {
    if (row.root_cause_verified) {
      return;
    }
    setEditing(row);
    form.setFieldsValue({
      root_cause_method: row.root_cause_method,
      root_cause_analysis: row.root_cause_analysis,
      root_cause_verified: row.root_cause_verified,
      root_cause_date: row.root_cause_date ? dayjs(row.root_cause_date) : dayjs(),
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (values: any) => {
    const payload = {
      root_cause_analysis: values.root_cause_analysis,
      root_cause_method: values.root_cause_method,
      root_cause_verified: !!values.root_cause_verified,
      root_cause_date: values.root_cause_date
        ? values.root_cause_date.format('YYYY-MM-DD')
        : undefined,
    };
    if (editing && editing.id !== 'legacy') {
      await updateMutation.mutateAsync({ ncrId, rcaId: editing.id, data: payload });
    } else {
      await addMutation.mutateAsync({ ncrId, data: payload });
    }
    setDrawerOpen(false);
    onChanged();
  };

  const handleReopen = async (row: NCRRootCauseAnalysis) => {
    if (row.id === 'legacy') return;
    await updateMutation.mutateAsync({ ncrId, rcaId: row.id, data: { reopen: true } });
    onChanged();
  };

  const handleDelete = async (row: NCRRootCauseAnalysis) => {
    if (row.id === 'legacy') return;
    await deleteMutation.mutateAsync({ ncrId, rcaId: row.id });
    onChanged();
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Root cause vs Correction vs CAPA"
        description="Root cause analysis explains why the non-conformance happened. Correction fixes this instance on the NCR. CAPA addresses systemic / preventive actions — create or link CAPAs from the CAPA tab."
      />
      {!canEditStatus && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Root cause analyses are read-only"
          description="This NCR is Closed or Rejected. Reopen / change status before editing RCAs."
        />
      )}
      {canEditStatus &&
        rows.some((r) => r.root_cause_verified) &&
        (ncr.status === 'CAPA Assigned' ||
          ncr.status === 'Corrective Action' ||
          ncr.status === 'Verification') && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Verified RCA is locked"
            description="Use Reopen to edit a verified analysis, or Add Root Cause Analysis to capture an additional finding."
          />
        )}
      <Space style={{ marginBottom: 16 }} wrap>
        <Title level={5} style={{ margin: 0 }}>
          Root Cause Analyses
        </Title>
        {canEditStatus && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add Root Cause Analysis
          </Button>
        )}
      </Space>

      {!rows.length ? (
        <Empty description="No root cause analyses yet">
          {canEditStatus && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              Add Root Cause Analysis
            </Button>
          )}
        </Empty>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {rows.map((row) => (
            <Card
              key={row.id}
              size="small"
              title={
                <Space>
                  <Text strong>#{row.sequence}</Text>
                  {row.root_cause_method && <Tag>{row.root_cause_method}</Tag>}
                  {row.root_cause_verified ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      Verified
                    </Tag>
                  ) : (
                    <Tag>Draft</Tag>
                  )}
                </Space>
              }
              extra={
                <Space>
                  {row.root_cause_verified && row.id !== 'legacy' && canEditStatus && (
                    <Button size="small" onClick={() => handleReopen(row)}>
                      Reopen
                    </Button>
                  )}
                  {!row.root_cause_verified && canEditStatus && row.id !== 'legacy' && (
                    <>
                      <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
                        Edit
                      </Button>
                      <Popconfirm title="Delete this root cause?" onConfirm={() => handleDelete(row)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </>
                  )}
                  {!row.root_cause_verified && canEditStatus && row.id === 'legacy' && (
                    <Button size="small" icon={<EditOutlined />} onClick={openAdd}>
                      Add structured RCA
                    </Button>
                  )}
                </Space>
              }
            >
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>
                {row.root_cause_analysis}
              </Paragraph>
              <Text type="secondary">
                {row.root_cause_date
                  ? `Date: ${dayjs(row.root_cause_date).format('DD/MM/YYYY')}`
                  : null}
              </Text>
            </Card>
          ))}
        </Space>
      )}

      <Drawer
        title={editing ? `Edit Root Cause #${editing.sequence}` : 'Add Root Cause Analysis'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={640}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={addMutation.isPending || updateMutation.isPending}
              onClick={() => form.submit()}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="root_cause_method" label="Method" rules={[{ required: true }]}>
            <Select
              options={RCA_METHODS.map((m) => ({ value: m, label: m }))}
              showSearch
            />
          </Form.Item>
          <Form.Item
            name="root_cause_analysis"
            label="Analysis"
            rules={[{ required: true, message: 'Enter the root cause analysis' }]}
          >
            <TextArea rows={8} placeholder="Describe the root cause findings…" />
          </Form.Item>
          <Form.Item name="root_cause_date" label="Analysis date">
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="root_cause_verified"
            label="Mark as verified"
            valuePropName="checked"
            extra="Verifying may advance NCR status and auto-create a CAPA when CAPA is required."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default NCRRootCausePanel;
